import React, { useEffect, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import type { ResolverConfigDto, ResolverInputDto } from "../../../../types/resolver";

function validateConfigPaste(parsed: unknown): { ok: true; config: ResolverConfigDto } | { ok: false; error: string } {
    if (parsed === null || typeof parsed !== "object") {
        return { ok: false, error: "Invalid JSON: expected an object." };
    }
    const obj = parsed as Record<string, unknown>;
    if (!Array.isArray(obj.nodes) || obj.nodes.length === 0) {
        return { ok: false, error: "Resolver config must have a non-empty nodes array." };
    }
    if (typeof obj.startNodeId !== "number") {
        return { ok: false, error: "Resolver config: startNodeId is required." };
    }
    const nodeIds = new Set((obj.nodes as Array<{ id: number }>).map((n) => n.id));
    if (!nodeIds.has(obj.startNodeId as number)) {
        return { ok: false, error: "Resolver config: startNodeId must reference an existing node id." };
    }
    return { ok: true, config: obj as unknown as ResolverConfigDto };
}

function validateInputPaste(parsed: unknown): { ok: true; input: ResolverInputDto } | { ok: false; error: string } {
    if (parsed === null || typeof parsed !== "object") {
        return { ok: false, error: "Invalid JSON: expected an object." };
    }
    const obj = parsed as Record<string, unknown>;
    if (!Array.isArray(obj.codes)) {
        return { ok: false, error: "Resolver input must have a codes array." };
    }
    if (typeof obj.workItemData !== "object" || obj.workItemData === null || Array.isArray(obj.workItemData)) {
        return { ok: false, error: "Resolver input: workItemData must be an object." };
    }
    return { ok: true, input: obj as unknown as ResolverInputDto };
}

export interface PasteConfigDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (config: ResolverConfigDto) => void;
}

export function PasteConfigDialog({ open, onClose, onConfirm }: PasteConfigDialogProps) {
    const [text, setText] = useState("");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setText("");
            setError(null);
        }
    }, [open]);

    const handleLoad = () => {
        setError(null);
        let parsed: unknown;
        try {
            parsed = JSON.parse(text.trim() || "{}");
        } catch {
            setError("Invalid JSON.");
            return;
        }
        setText(JSON.stringify(parsed, null, 2));
        const result = validateConfigPaste(parsed);
        if (result.ok === false) {
            setError(result.error);
            return;
        }
        try {
            onConfirm(result.config);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Paste config</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Paste a resolver config as JSON (nodes, startNodeId, name, etc.). Minified or pretty-printed is
                    accepted.
                </Typography>
                <TextField
                    multiline
                    minRows={8}
                    maxRows={16}
                    fullWidth
                    placeholder='{"resolverVersionMajor":1,"name":"...","nodes":[...],"startNodeId":1}'
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    sx={{ fontFamily: "monospace", fontSize: 12 }}
                    inputProps={{ "data-testid": "paste-config-json-input" }}
                />
                {error && (
                    <Alert severity="error" sx={{ mt: 1 }} data-testid="paste-config-error">
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleLoad} data-testid="paste-config-submit">
                    Load
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export interface PasteInputDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (input: ResolverInputDto) => void;
}

export function PasteInputDialog({ open, onClose, onConfirm }: PasteInputDialogProps) {
    const [text, setText] = useState("");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setText("");
            setError(null);
        }
    }, [open]);

    const handleLoad = () => {
        setError(null);
        let parsed: unknown;
        try {
            parsed = JSON.parse(text.trim() || "{}");
        } catch {
            setError("Invalid JSON.");
            return;
        }
        setText(JSON.stringify(parsed, null, 2));
        const result = validateInputPaste(parsed);
        if (result.ok === false) {
            setError(result.error);
            return;
        }
        try {
            onConfirm(result.input);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Paste input</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Paste resolver input as JSON (codes, workItemData). Minified or pretty-printed is accepted.
                </Typography>
                <TextField
                    multiline
                    minRows={8}
                    maxRows={16}
                    fullWidth
                    placeholder='{"codes":[...],"workItemData":{...}}'
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    sx={{ fontFamily: "monospace", fontSize: 12 }}
                    inputProps={{ "data-testid": "paste-input-json-input" }}
                />
                {error && (
                    <Alert severity="error" sx={{ mt: 1 }} data-testid="paste-input-error">
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleLoad} data-testid="paste-input-submit">
                    Load
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export interface DeleteConfigDialogProps {
    open: boolean;
    configName: string;
    onClose: () => void;
    onConfirm: () => void;
}

export function DeleteConfigDialog({ open, configName, onClose, onConfirm }: DeleteConfigDialogProps) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            aria-labelledby="delete-resolver-config-title"
        >
            <DialogTitle id="delete-resolver-config-title">Delete resolver config?</DialogTitle>
            <DialogContent>
                <Typography variant="body2">
                    This will permanently delete the configuration &quot;{configName}&quot;. This action cannot be
                    undone.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" color="error" onClick={onConfirm} data-testid="resolver-config-delete-confirm">
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
}
